import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  CalendarClock, CalendarDays, Check, ChevronDown, ChevronRight, Clapperboard, ClipboardCheck, Clock, Code2, CreditCard, Eye, FileText, Globe2, Heart, HelpCircle, Hourglass, Image as ImageIcon, Link2, Lock, MessageCircle, MonitorPlay, Play, Share2, ShoppingCart, Smartphone, Star, UserPlus, Youtube, Trophy,
} from 'lucide-react';
import { toast } from 'sonner';
import { engageAPI, learnAPI, publicAPI, shopAPI } from '../lib/api';
import { getUser, isLoggedIn } from '../lib/auth';
import { notifyCartChanged } from './CartPage';
import { Field, Modal, btnGhost, btnPrimary, inputCls } from '../components/ui';
import { getLang, t } from '../lib/i18n';
import { Price } from '../components/CourseCard';
import { pointsAPI, pointsFor, useLmsActions } from '../lib/points';
import CouponField, { useReleaseOnCancel } from '../components/CouponField';

// Course landing page (s134 redesign, Udemy register): full-width dark header
// band, a stats strip riding its bottom edge, a floating purchase card on the
// right (sticky), then What you'll learn / Related topics / This course
// includes / Course content (accordion) / Requirements / Description / Reviews.

const TYPE_ICON = {
  video: Clapperboard, youtube: Youtube, vimeo: MonitorPlay, url: Link2, iframe: MonitorPlay,
  document: FileText, image: ImageIcon, quiz: HelpCircle, wealth_scorecard: ClipboardCheck, html: Code2,
};
const VIDEO_TYPES = ['video', 'youtube', 'vimeo'];
const press = 'lms-press';

function CurriculumSection({ section, open, onToggle }) {
  return (
    <div className="border-b border-lms-line last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 bg-lms-raised px-5 py-4 text-left hover:brightness-[0.98]"
      >
        <ChevronDown size={16} className={`shrink-0 text-lms-ink transition-transform ${open ? 'rotate-180' : ''}`} />
        <span className="flex-1 text-[15.5px] font-bold text-lms-ink">{section.title}</span>
        <span className="text-sm text-lms-body lms-num">
          {section.lessons.length} {section.lessons.length === 1 ? t('lesson') : t('lessons')}
        </span>
      </button>
      {open && (
        <div className="border-t border-lms-line px-5 py-2">
          {section.lessons.map((l) => {
            const Icon = TYPE_ICON[l.lesson_type] || FileText;
            return (
              <div key={l.id} className="flex items-center gap-3.5 py-2.5 text-sm text-lms-body">
                <Icon size={16} className="shrink-0" />
                <span className="min-w-0 flex-1">{l.title}</span>
                {l.duration && <span className="text-xs text-lms-sub lms-num">{l.duration}</span>}
                <Lock size={13} className="shrink-0 text-lms-faint" aria-label={t('Locked')} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CoursePage() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const lmsActions = useLmsActions();
  const coursePts = pointsFor(lmsActions, 'course_completed');
  const [missing, setMissing] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [enrolled, setEnrolled] = useState(false);
  const [pending, setPending] = useState(false); // s135: registration awaiting approval
  const [enrollChecked, setEnrollChecked] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rating, setRating] = useState(false);
  const [stars, setStars] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [messaging, setMessaging] = useState(false);
  const [msgDraft, setMsgDraft] = useState('');
  const [descOpen, setDescOpen] = useState(false);
  const [coupon, setCoupon] = useState(null); // s136: validated coupon preview
  useReleaseOnCancel();

  useEffect(() => {
    setCourse(null);
    setEnrolled(false);
    setPending(false);
    setEnrollChecked(false);
    setWishlisted(false);
    publicAPI.course(slug)
      .then(({ data }) => {
        setCourse(data);
        setOpenSections(Object.fromEntries((data.curriculum || []).slice(0, 1).map((s) => [s.id, true])));
        // staff always have player access; members check their library
        const user = getUser();
        if (['admin', 'manager'].includes(user?.lr) || data.preview) {
          setEnrolled(true);
          setEnrollChecked(true);
        } else if (isLoggedIn()) {
          learnAPI.myCourses()
            .then(({ data: mine }) => {
              const row = mine.courses.find((c) => c.id === data.id);
              setPending(!!row && row.enrollment_status === 'pending');
              setEnrolled(!!row && row.enrollment_status !== 'pending');
            })
            .catch(() => {})
            .finally(() => setEnrollChecked(true));
        } else {
          setEnrollChecked(true);
        }
        if (isLoggedIn()) {
          shopAPI.wishlist()
            .then(({ data: w }) => setWishlisted((w.course_ids || []).includes(data.id)))
            .catch(() => {});
        }
      })
      .catch(() => setMissing(true));
  }, [slug]);

  // s135: free courses are not self-serve — Register creates a pending
  // registration that staff approve in Admin → Enrollments.
  const register = async () => {
    setBusy(true);
    try {
      const { data } = await shopAPI.register(course.id);
      if (data.status === 'pending') {
        setPending(true);
        toast.success(t('Registration sent. You will get an email once it is approved.'));
      } else {
        setEnrolled(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const addToCart = async () => {
    setBusy(true);
    try {
      await shopAPI.cartAdd(course.id);
      notifyCartChanged();
      toast.success(t('Added to cart'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const buyNow = async () => {
    setBusy(true);
    try {
      const { data } = await shopAPI.checkout([course.id], coupon ? coupon.code : '', `/course/${course.slug}`);
      if (data.free) { // 100 % coupon: enrolled without Stripe
        toast.success(t('You are enrolled!'));
        navigate(`/learn/${course.slug}`);
        return;
      }
      window.location.assign(data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Checkout failed'));
      setBusy(false);
    }
  };

  const toggleWishlist = async () => {
    try {
      const { data } = await shopAPI.wishlistToggle(course.id);
      setWishlisted(data.wishlisted);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    }
  };

  const openRate = async () => {
    try {
      const { data } = await engageAPI.myRating(course.id);
      if (data.rating) {
        setStars(data.rating.rating);
        setReviewText(data.rating.review || '');
      }
    } catch {
      /* fresh rating */
    }
    setRating(true);
  };

  // "Leave a rating" from My courses lands here with ?rate=1.
  useEffect(() => {
    if (!course || !enrollChecked || params.get('rate') !== '1') return;
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('rate');
      return next;
    }, { replace: true });
    if (enrolled) openRate();
  }, [course, enrollChecked, enrolled]);

  const submitRating = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await engageAPI.rate(course.id, { rating: stars, review: reviewText });
      toast.success(t('Thanks for your review!'));
      setRating(false);
      publicAPI.course(slug).then(({ data }) => setCourse(data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const messageInstructor = async (e) => {
    e.preventDefault();
    const text = msgDraft.trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await engageAPI.startThread(course.instructor.member_id, text);
      navigate(`/my/messages?t=${data.thread_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
      setBusy(false);
    }
  };

  const share = async () => {
    const url = window.location.href.split('?')[0];
    try {
      if (navigator.share) await navigator.share({ title: course.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success(t('Link copied'));
      }
      if (isLoggedIn()) pointsAPI.share(course.id).catch(() => {});
    } catch {
      /* dismissed */
    }
  };

  const includes = useMemo(() => {
    if (!course) return [];
    const lessons = (course.curriculum || []).flatMap((s) => s.lessons || []);
    const count = (pred) => lessons.filter(pred).length;
    const videos = count((l) => VIDEO_TYPES.includes(l.lesson_type));
    const docs = count((l) => l.lesson_type === 'document');
    const pages = count((l) => l.lesson_type === 'html');
    const quizzes = count((l) => l.lesson_type === 'quiz');
    const scorecard = count((l) => l.lesson_type === 'wealth_scorecard');
    const sections = (course.curriculum || []).length;
    const out = [[Clapperboard, `${course.lesson_count || 0} ${t('lessons')}${sections > 1 ? ` · ${sections} ${t('sections')}` : ''}`]];
    if (videos) out.push([MonitorPlay, `${videos} ${videos === 1 ? t('video lesson') : t('video lessons')}`]);
    if (docs) out.push([FileText, `${docs} ${docs === 1 ? t('document') : t('documents')}`]);
    if (pages) out.push([Code2, `${pages} ${pages === 1 ? t('interactive lesson') : t('interactive lessons')}`]);
    if (quizzes) out.push([HelpCircle, `${quizzes} ${quizzes === 1 ? t('quiz') : t('quizzes')}`]);
    if (scorecard) out.push([ClipboardCheck, t('Wealth Baseline Scorecard')]);
    out.push([CalendarClock, t('Sessions with your mentor')]);
    out.push([Clock, t('Full lifetime access')]);
    out.push([Smartphone, t('Learn on any device')]);
    return out;
  }, [course]);

  if (missing) {
    return (
      <div className="px-6 py-16 text-center text-sm text-lms-sub">
        {t('This course does not exist or is not published.')}{' '}
        <Link to="/courses" className="text-lms-link">{t('Browse courses')}</Link>
      </div>
    );
  }
  if (!course) return <p className="p-6 text-sm text-lms-muted">{t('Loading…')}</p>;

  const loggedIn = isLoggedIn();
  const isFree = !!course.is_free;
  const r = course.rating || {};
  const sections = course.curriculum || [];
  const allOpen = sections.length > 0 && sections.every((s) => openSections[s.id]);
  const updated = course.updated_at || course.created_at;
  const updatedLabel = updated
    ? new Date(updated).toLocaleDateString(getLang() === 'es' ? 'es' : 'en', { month: 'long', year: 'numeric' })
    : '';
  const langLabel = ({ en: 'English', es: 'Español' })[(course.language || 'en').toLowerCase()] || (course.language || '').toUpperCase();

  const primaryCta = 'flex h-12 w-full items-center justify-center gap-2 rounded-[10px] bg-lms-accent text-[15px] font-bold text-lms-accent-ink hover:brightness-110 disabled:opacity-60';
  const secondaryCta = 'flex h-12 w-full items-center justify-center gap-2 rounded-[10px] border border-lms-ink text-[15px] font-bold text-lms-ink hover:bg-lms-raised disabled:opacity-60';

  const purchaseCard = (
    <div className="overflow-hidden rounded-xl border border-lms-line bg-lms-surface shadow-[var(--lms-elev-3)]">
      <div className="relative">
        {course.thumbnail ? (
          <img src={course.thumbnail} alt="" className="aspect-video w-full object-cover" />
        ) : (
          <div className="flex aspect-video w-full items-center justify-center bg-lms-sidebar p-6 text-center">
            {!enrolled && <span className="text-xl font-bold leading-snug text-white">{course.title}</span>}
          </div>
        )}
        {enrolled && (
          <Link
            to={`/learn/${course.slug}`}
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/35 hover:bg-black/45"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-lms-chip-bg text-lms-chip-ink">
              <Play size={26} fill="currentColor" />
            </span>
            <span className="text-[15px] font-bold text-white">{t('Open the course')}</span>
          </Link>
        )}
      </div>
      <div className="flex flex-col gap-3.5 p-6">
        <div className="text-[30px] leading-none">
          {coupon ? (
            <span className="lms-num">
              <span className="mr-1.5 text-[0.55em] font-normal text-lms-faint line-through">${Number(coupon.subtotal).toFixed(2)}</span>
              <span className="font-bold text-lms-ink">${Number(coupon.total).toFixed(2)}</span>
            </span>
          ) : <Price course={course} />}
        </div>
        {!enrollChecked ? (
          // Until we know whether this member owns the course, show a neutral
          // placeholder — never a Buy now that flips to Go to course (s136).
          <div aria-hidden="true" className="h-12 w-full animate-pulse rounded-[10px] bg-lms-raised motion-reduce:animate-none" />
        ) : enrolled ? (
          <Link to={`/learn/${course.slug}`} className={`${press} ${primaryCta}`}>{t('Go to course')}</Link>
        ) : !loggedIn ? (
          <Link to="/login" className={`${press} ${primaryCta}`}>{isFree ? t('Sign in to register') : t('Sign in to enroll')}</Link>
        ) : pending ? (
          <div role="status" className="flex flex-col items-center gap-1 rounded-[10px] border border-lms-line bg-lms-raised px-4 py-3 text-center">
            <span className="flex items-center gap-2 text-[15px] font-bold text-lms-ink"><Hourglass size={16} /> {t('Registration pending')}</span>
            <span className="text-[13px] text-lms-sub">{t('Your request was sent. You will get an email once it is approved.')}</span>
          </div>
        ) : isFree ? (
          <button type="button" onClick={register} disabled={busy} className={`${press} ${primaryCta}`}>
            <UserPlus size={16} /> {busy ? t('Sending…') : t('Register')}
          </button>
        ) : (
          <>
            <CouponField courseIds={[course.id]} applied={coupon} onApplied={setCoupon} compact />
            <button type="button" onClick={buyNow} disabled={busy} className={`${press} ${primaryCta}`}>
              <CreditCard size={16} /> {coupon && coupon.total <= 0 ? t('Enroll now') : t('Buy now')}
            </button>
            {!coupon && (
              <button type="button" onClick={addToCart} disabled={busy} className={`${press} ${secondaryCta}`}>
                <ShoppingCart size={16} /> {t('Add to cart')}
              </button>
            )}
          </>
        )}
        {enrollChecked && loggedIn && !enrolled && !pending && (
          <button type="button" onClick={toggleWishlist} className={`${press} ${secondaryCta}`} aria-pressed={wishlisted}>
            <Heart size={16} fill={wishlisted ? 'currentColor' : 'none'} />
            {wishlisted ? t('In your wishlist') : t('Add to wishlist')}
          </button>
        )}
        <ul className="flex flex-col gap-2.5 pt-1.5 text-sm text-lms-body">
          <li className="flex items-center gap-2.5"><Clock size={16} />{t('Full lifetime access')}</li>
          <li className="flex items-center gap-2.5"><Smartphone size={16} />{t('Learn on any device')}</li>
          <li className="flex items-center gap-2.5"><CalendarClock size={16} />{t('Book sessions with your mentor')}</li>
          {coursePts > 0 && (
            <li className="flex items-center gap-2.5"><Trophy size={16} />{t('Earn')} <span className="font-semibold lms-num">{coursePts.toLocaleString()}</span> {t('points when you complete it')}</li>
          )}
        </ul>
        {loggedIn && course.instructor && course.instructor.member_id !== (getUser() || {}).mid && (
          <button type="button" onClick={() => setMessaging(true)} className="flex items-center gap-2 text-sm font-semibold text-lms-link hover:underline">
            <MessageCircle size={15} /> {t('Message the instructor')}
          </button>
        )}
      </div>
      <div className="flex items-center justify-between border-t border-lms-line bg-lms-raised px-6 py-2.5">
        {loggedIn && enrolled ? (
          <button type="button" onClick={openRate} className="flex items-center gap-1.5 text-sm font-semibold text-lms-link hover:underline">
            <Star size={15} /> {t('Rate this course')}
          </button>
        ) : <span />}
        <button type="button" onClick={share} aria-label={t('Share')} className={`${press} flex h-11 w-11 items-center justify-center text-lms-ink`}>
          <Share2 size={18} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-full bg-lms-surface">
      {course.preview && (
        <div role="status" className="flex items-center justify-center gap-2 bg-lms-tag-alt-bg px-4 py-2.5 text-center text-[13.5px] font-semibold text-lms-tag-alt-ink">
          <Eye size={15} />
          {t('Preview')} · {t(course.status === 'draft' ? 'Draft' : course.status === 'pending' ? 'Pending review' : 'Unpublished')} — {t('students can’t see this course yet.')}
        </div>
      )}
      {/* ===== DARK HEADER BAND ===== */}
      <section className="bg-lms-band">
        <div className="mx-auto max-w-[1248px] px-6 pb-[72px] pt-9 lg:pr-[420px]">
          <div className="flex flex-col gap-4">
            <nav aria-label={t('Breadcrumb')} className="flex flex-wrap items-center gap-2.5 text-sm font-semibold">
              <Link to="/courses" className="text-lms-band-link hover:underline">{t('Courses')}</Link>
              {course.category_name && (
                <>
                  <ChevronRight size={14} className="text-lms-band-muted" />
                  <Link to={`/?category=${course.category_id}`} className="text-lms-band-link hover:underline">{course.category_name}</Link>
                </>
              )}
              {course.sub_category_name && (
                <>
                  <ChevronRight size={14} className="text-lms-band-muted" />
                  <Link to={`/?category=${course.sub_category_id}`} className="text-lms-band-link hover:underline">{course.sub_category_name}</Link>
                </>
              )}
            </nav>
            <h1 className="text-[28px] font-bold leading-tight text-lms-band-text sm:text-4xl">{course.title}</h1>
            {course.short_description && (
              <p className="text-lg leading-relaxed text-lms-band-sub">{course.short_description}</p>
            )}
            <div className="flex flex-wrap gap-2">
              {isFree && <span className="rounded-md bg-lms-tag-free-bg px-2.5 py-1 text-[12.5px] font-bold text-lms-tag-free-ink">{t('Free')}</span>}
              {course.level && <span className="rounded-md bg-lms-tag-alt-bg px-2.5 py-1 text-[12.5px] font-bold capitalize text-lms-tag-alt-ink">{t(course.level)}</span>}
              {r.count > 0 && (
                <span className="flex items-center gap-1.5 text-sm text-lms-band-sub">
                  <span className="font-bold text-lms-rating lms-num">{r.avg}</span>
                  <Star size={14} className="text-lms-rating" fill="currentColor" />
                  <span className="lms-num">({r.count} {r.count === 1 ? t('rating') : t('ratings')})</span>
                </span>
              )}
            </div>
            {course.instructor && (
              <p className="text-[14.5px] text-lms-band-text">
                {t('Created by')}{' '}
                <Link to={`/instructor/${course.instructor.member_id}`} className="text-lms-band-link underline">{course.instructor.name}</Link>
              </p>
            )}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-lms-band-sub">
              {updatedLabel && (
                <span className="flex items-center gap-1.5"><CalendarDays size={15} />{t('Last updated')} {updatedLabel}</span>
              )}
              <span className="flex items-center gap-1.5"><Globe2 size={15} />{langLabel}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FLOATING PURCHASE CARD (desktop): anchored to the page top so it
          overlaps the band whatever the title length, then sticks on scroll. ===== */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 top-9 z-20 hidden lg:block">
        <div className="relative mx-auto h-full max-w-[1248px] px-6">
          <div className="pointer-events-auto absolute bottom-0 right-6 top-0 w-[356px]">
            <div className="sticky top-[84px]">{purchaseCard}</div>
          </div>
        </div>
      </div>

      <div className="relative mx-auto max-w-[1248px] px-6">

        <div className="lg:pr-[396px]">
          {/* ===== STATS STRIP riding the band edge ===== */}
          <div className="-mt-10 grid grid-cols-3 rounded-xl border border-lms-line bg-lms-surface shadow-[var(--lms-elev-2)]">
            <div className="border-r border-lms-line px-4 py-[18px] text-center">
              <div className="text-2xl font-bold text-lms-ink lms-num">{sections.length}</div>
              <div className="text-[13px] text-lms-sub">{sections.length === 1 ? t('section') : t('sections')}</div>
            </div>
            <div className="border-r border-lms-line px-4 py-[18px] text-center">
              <div className="text-2xl font-bold text-lms-ink lms-num">{course.lesson_count || 0}</div>
              <div className="text-[13px] text-lms-sub">{t('lessons')}</div>
            </div>
            <div className="px-4 py-[18px] text-center">
              {r.count > 0 ? (
                <>
                  <div className="text-2xl font-bold text-lms-ink lms-num">{r.avg}</div>
                  <div className="text-[13px] text-lms-sub">{r.count} {r.count === 1 ? t('rating') : t('ratings')}</div>
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold text-lms-ink">{langLabel}</div>
                  <div className="text-[13px] text-lms-sub">{t('language')}</div>
                </>
              )}
            </div>
          </div>

          {/* mobile purchase card */}
          <div className="mt-6 lg:hidden">{purchaseCard}</div>

          <div className="flex flex-col gap-10 pb-16 pt-9">
            {(course.outcomes || []).length > 0 && (
              <section className="flex flex-col gap-4 rounded-xl border border-lms-line p-6 sm:px-7">
                <h2 className="text-2xl font-bold text-lms-ink">{t("What you'll learn")}</h2>
                <ul className="grid gap-x-8 gap-y-3.5 sm:grid-cols-2">
                  {course.outcomes.map((o, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-lms-body">
                      <Check size={16} strokeWidth={2.6} className="mt-[3px] shrink-0 text-lms-ink" />
                      {o}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {(course.category_name || course.sub_category_name) && (
              <section className="flex flex-col gap-3.5">
                <h2 className="text-2xl font-bold text-lms-ink">{t('Related topics')}</h2>
                <div className="flex flex-wrap gap-2.5">
                  {[[course.category_id, course.category_name], [course.sub_category_id, course.sub_category_name]]
                    .filter(([, n]) => n)
                    .map(([id, n]) => (
                      <Link key={id} to={`/?category=${id}`} className={`${press} rounded-lg border border-lms-ink px-4 py-2.5 text-sm font-bold text-lms-ink hover:bg-lms-raised`}>
                        {n}
                      </Link>
                    ))}
                </div>
              </section>
            )}

            <section className="flex flex-col gap-3.5">
              <h2 className="text-2xl font-bold text-lms-ink">{t('This course includes:')}</h2>
              <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
                {includes.map(([Icon, label]) => (
                  <li key={label} className="flex items-center gap-3 text-[14.5px] text-lms-body">
                    <Icon size={17} className="shrink-0" />{label}
                  </li>
                ))}
              </ul>
            </section>

            {sections.length > 0 && (
              <section className="flex flex-col gap-3">
                <h2 className="text-2xl font-bold text-lms-ink">{t('Course content')}</h2>
                <div className="flex items-center justify-between text-sm text-lms-body">
                  <span className="lms-num">
                    {sections.length} {sections.length === 1 ? t('section') : t('sections')} · {course.lesson_count || 0} {t('lessons')}
                  </span>
                  <button
                    type="button"
                    onClick={() => setOpenSections(allOpen ? {} : Object.fromEntries(sections.map((s) => [s.id, true])))}
                    className="font-bold text-lms-link hover:underline"
                  >
                    {allOpen ? t('Collapse all sections') : t('Expand all sections')}
                  </button>
                </div>
                <div className="overflow-hidden rounded-lg border border-lms-line">
                  {sections.map((s) => (
                    <CurriculumSection
                      key={s.id}
                      section={s}
                      open={!!openSections[s.id]}
                      onToggle={() => setOpenSections((prev) => ({ ...prev, [s.id]: !prev[s.id] }))}
                    />
                  ))}
                </div>
              </section>
            )}

            {(course.requirements || []).length > 0 && (
              <section className="flex flex-col gap-3">
                <h2 className="text-2xl font-bold text-lms-ink">{t('Requirements')}</h2>
                <ul className="list-disc space-y-1.5 pl-5 text-[14.5px] leading-relaxed text-lms-body">
                  {course.requirements.map((req, i) => <li key={i}>{req}</li>)}
                </ul>
              </section>
            )}

            {course.description && (
              <section className="flex flex-col gap-3">
                <h2 className="text-2xl font-bold text-lms-ink">{t('Description')}</h2>
                <div
                  className={`lms-prose ${descOpen ? '' : 'max-h-[220px] overflow-hidden'}`}
                  dangerouslySetInnerHTML={{ __html: course.description }}
                />
                <button type="button" onClick={() => setDescOpen((o) => !o)} className="flex items-center gap-1 self-start text-sm font-bold text-lms-link hover:underline">
                  {descOpen ? t('Show less') : t('Show more')}
                  <ChevronDown size={15} className={descOpen ? 'rotate-180' : ''} />
                </button>
              </section>
            )}

            {(course.reviews || []).length > 0 && (
              <section className="flex flex-col gap-3.5">
                <h2 className="text-2xl font-bold text-lms-ink">{t('Reviews')}</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {course.reviews.map((rv, i) => (
                    <div key={i} className="flex flex-col gap-2 border-t border-lms-line pt-4">
                      <div className="flex gap-0.5 text-lms-rating">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} size={14} fill={n <= (rv.rating || 0) ? 'currentColor' : 'none'} />
                        ))}
                      </div>
                      {rv.review && <p className="text-sm leading-relaxed text-lms-body">{rv.review}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>

      {rating && (
        <Modal title={t('Rate this course')} onClose={() => setRating(false)}>
          <form onSubmit={submitRating} className="space-y-4">
            <div className="flex justify-center gap-1.5 py-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setStars(n)} aria-label={`${n}`}>
                  <Star size={26} className={n <= stars ? 'text-lms-rating' : 'text-lms-faint'} fill={n <= stars ? 'currentColor' : 'none'} />
                </button>
              ))}
            </div>
            <Field label={t('Your review (optional)')}>
              <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} rows={4} className={inputCls} />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setRating(false)} className={btnGhost}>{t('Cancel')}</button>
              <button type="submit" disabled={busy} className={btnPrimary}>
                {busy ? t('Saving…') : t('Submit review')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {messaging && (
        <Modal title={`${t('Message')} ${course.instructor?.name || ''}`} onClose={() => setMessaging(false)}>
          <form onSubmit={messageInstructor} className="space-y-4">
            <Field label={t('Your message')}>
              <textarea value={msgDraft} onChange={(e) => setMsgDraft(e.target.value)} rows={4} required autoFocus className={inputCls} />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setMessaging(false)} className={btnGhost}>{t('Cancel')}</button>
              <button type="submit" disabled={busy || !msgDraft.trim()} className={btnPrimary}>
                {busy ? t('Sending…') : t('Send')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
